import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import styled from 'styled-components';

import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { LeaderboardRewards } from '#src/components/leaderboard/LeaderboardRewards/index.tsx';

import type {
  LazyRankRewardsQuery,
  LazyRankRewardsQueryVariables,
} from './__generated__/index.graphql.ts';

const Root = styled.div`
  &.loading {
    min-height: 400px;
  }
`;

const LAZY_REWARDS_QUERY = gql`
  query LazyRankRewardsQuery($so5LeaderboardSlug: String!) {
    so5 {
      so5Leaderboard(slug: $so5LeaderboardSlug) {
        slug
        ...LeaderboardRewards_so5Leaderboard
      }
    }
  }
  ${LeaderboardRewards.fragments.so5Leaderboard}
` as TypedDocumentNode<LazyRankRewardsQuery, LazyRankRewardsQueryVariables>;

type Props = {
  so5LeaderboardSlug: string;
} & Pick<
  ComponentProps<typeof LeaderboardRewards>,
  | 'withSeparator'
  | 'hideThresholds'
  | 'onClickRanking'
  | 'selectedContender'
  | 'displayAlternativeRewards'
>;

export const LazyRewards = ({ so5LeaderboardSlug, ...rest }: Props) => {
  const { data, loading } = useQuery(LAZY_REWARDS_QUERY, {
    variables: { so5LeaderboardSlug },
  });

  if (loading) {
    return (
      <Root className="loading">
        <LoadingIndicator />
      </Root>
    );
  }

  const { so5Leaderboard } = data?.so5 || {};

  if (!so5Leaderboard) {
    return null;
  }

  return <LeaderboardRewards so5Leaderboard={so5Leaderboard} {...rest} />;
};
